import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Space, Select, DatePicker, message, InputNumber, Table, Input, Alert } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
// Using Ant Design's Table directly here for flexibility (expandable rows, custom renderers)
import { useGetMatGapWhInventory, useGetMatWarehouses, useGetMatGapIssueRequests, useGetVwMatGapRequestItemOutstanding } from '../../../hooks/genericHooks';
import type { VwMatGapWhInventory, MatWarehouses, MatGapIssueRequest, VwMatGapRequestItemOutstanding } from '../../../types';
import { insertData, getNextSequenceValue, updateData } from '../../../services/api';
import { useAuth } from '../../../components/common/security/AuthContext';
import PersonnelSelect from '../../../components/modals/PersonnelSelect';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

// Fresh request-centric UI; no legacy inventory-row table below

export default function MatGapTxnIssue() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: invData = [], isLoading, refetch: refetchInventory } = useGetMatGapWhInventory() as any;
  const { data: whData = [] } = useGetMatWarehouses() as any;
  const { data: reqHdrData = [], refetch: refetchReqHeaders } = useGetMatGapIssueRequests() as any;
  const { data: reqOutData = [], refetch: refetchOutstanding } = useGetVwMatGapRequestItemOutstanding() as any;

  const inventory: VwMatGapWhInventory[] = Array.isArray(invData) ? invData : (invData?.data ?? []);
  const warehouses: MatWarehouses[] = Array.isArray(whData) ? whData : (whData?.data ?? []);
  const { user: currentUser } = useAuth();
  const reqHeaders: MatGapIssueRequest[] = Array.isArray(reqHdrData) ? reqHdrData : (reqHdrData?.data ?? []);

  const whMap = useMemo(() => {
    const m = new Map<number, string>();
    (warehouses || []).forEach(w => m.set(w.id, w.name));
    return m;
  }, [warehouses]);

  // Material description map from inventory view
  const descMap = useMemo(() => {
    const m = new Map<string, string | null>();
    (inventory || []).forEach(r => {
      const key = String(r.mat_id);
      if (!m.has(key)) m.set(key, r.description || null);
    });
    return m;
  }, [inventory]);

  // ===================== NEW: Request-centric Fulfill UI (fresh) =====================
  const [selectedRefNo, setSelectedRefNo] = useState<string | undefined>(undefined);
  const [issueDate, setIssueDate] = useState<string | null>(dayjs().format('YYYY-MM-DD'));
  const [fromWarehouseId, setFromWarehouseId] = useState<number | undefined>(undefined);

  // Personnel selection state
  const [personnelOpen, setPersonnelOpen] = useState(false);
  const [personnelDisplay, setPersonnelDisplay] = useState<string>('');
  const [personnelCode, setPersonnelCode] = useState<string | null>(null);

  // Always refresh the approved/partial request list on page entry
  useEffect(() => {
    if (typeof refetchReqHeaders === 'function') {
      refetchReqHeaders();
    }
    if (typeof refetchOutstanding === 'function') {
      refetchOutstanding();
    }
    // We intentionally do this only on first mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize from navigation state (if provided)
  useEffect(() => {
    try {
      const st: any = (location as any)?.state || {};
      if (st?.ref_no) setSelectedRefNo(String(st.ref_no));
      if (st?.from_warehouse_id != null) setFromWarehouseId(Number(st.from_warehouse_id));
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const requestOptions = useMemo(() => {
    const headers: MatGapIssueRequest[] = Array.isArray(reqHdrData) ? reqHdrData : (reqHdrData?.data ?? []);
    // If no warehouse selected, no options (enforce cascade)
    if (!fromWarehouseId) return [] as { value: string; label: string }[];
    const allOut: VwMatGapRequestItemOutstanding[] = Array.isArray(reqOutData) ? (reqOutData as any) : (reqOutData?.data ?? []);
    const allowedRefs = new Set<string>();
    (allOut || []).forEach(r => {
      if (Number(r.requested_wh_id) === Number(fromWarehouseId) && r.ref_no) {
        allowedRefs.add(String(r.ref_no).trim());
      }
    });
    return headers
      .filter(h => (h.status === 'approved' || h.status === 'partially_issued') && allowedRefs.has(String(h.ref_no).trim()))
      .map(h => ({ value: h.ref_no, label: `${h.ref_no} - ${h.requester_name || h.requester || ''}` }));
  }, [reqHdrData, reqOutData, fromWarehouseId]);

  // If current selected request is not valid for selected warehouse, clear it
  useEffect(() => {
    if (!selectedRefNo) return;
    const ok = requestOptions.some(opt => String(opt.value) === String(selectedRefNo));
    if (!ok) setSelectedRefNo(undefined);
  }, [fromWarehouseId, requestOptions]);

  const outstandingItems = useMemo(() => {
    if (!selectedRefNo) return [] as VwMatGapRequestItemOutstanding[];
    const sel = String(selectedRefNo).trim();
    const all: VwMatGapRequestItemOutstanding[] = Array.isArray(reqOutData) ? (reqOutData as any) : (reqOutData?.data ?? []);
    return all.filter(r => String(r.ref_no || '').trim() === sel);
  }, [reqOutData, selectedRefNo]);

  // Apply top-level warehouse restriction: only show items requested from the selected warehouse
  const visibleItems = useMemo(() => {
    if (!fromWarehouseId) return [] as VwMatGapRequestItemOutstanding[];
    return (outstandingItems || []).filter(r => Number(r.requested_wh_id) === Number(fromWarehouseId));
  }, [outstandingItems, fromWarehouseId]);

  // Distinct warehouses involved in the selected request
  const requestWarehouses = useMemo(() => {
    const s = new Set<number>();
    (outstandingItems || []).forEach(r => { if (r.requested_wh_id != null) s.add(Number(r.requested_wh_id)); });
    return s;
  }, [outstandingItems]);

  // Ownership guard: current user must be in selected warehouse owners
  const isOwner = useMemo(() => {
    if (!fromWarehouseId || !currentUser) return false;
    const wh = (warehouses || []).find(w => Number(w.id) === Number(fromWarehouseId));
    const owners: string[] = Array.isArray(wh?.owners) ? (wh?.owners as string[]) : [];
    return owners.includes(String(currentUser));
  }, [fromWarehouseId, currentUser, warehouses]);
  const canTransact = Boolean(fromWarehouseId && currentUser && isOwner);

  // Owned warehouses set for current user
  const ownedWhSet = useMemo(() => {
    const s = new Set<number>();
    (warehouses || []).forEach(w => {
      const owners: string[] = Array.isArray(w?.owners) ? (w.owners as string[]) : [];
      if (currentUser && owners.includes(String(currentUser))) s.add(Number(w.id));
    });
    return s;
  }, [warehouses, currentUser]);

  // Warehouses that have any outstanding items across all requests
  const allowedWhSet = useMemo(() => {
    const s = new Set<number>();
    const all: VwMatGapRequestItemOutstanding[] = Array.isArray(reqOutData) ? (reqOutData as any) : (reqOutData?.data ?? []);
    (all || []).forEach(r => { if (r.requested_wh_id != null && Number(r.outstanding_qty || 0) > 0) s.add(Number(r.requested_wh_id)); });
    return s;
  }, [reqOutData]);

  const fromWarehouseOptions = useMemo(() => {
    return (warehouses || [])
      .filter(w => allowedWhSet.has(Number(w.id)))
      .map(w => ({ value: w.id, label: w.name, disabled: !ownedWhSet.has(Number(w.id)) }));
  }, [warehouses, allowedWhSet, ownedWhSet]);

  // Clear fromWarehouseId if it is no longer allowed
  useEffect(() => {
    if (fromWarehouseId != null && (!allowedWhSet.has(Number(fromWarehouseId)) || !ownedWhSet.has(Number(fromWarehouseId)))) {
      setFromWarehouseId(undefined);
    }
  }, [allowedWhSet, ownedWhSet]);

  // If a request is selected and it targets a single warehouse, preselect it
  useEffect(() => {
    if (selectedRefNo && requestWarehouses.size === 1) {
      const onlyWh = Array.from(requestWarehouses)[0];
      if (onlyWh != null && fromWarehouseId !== onlyWh) setFromWarehouseId(onlyWh);
    }
  }, [selectedRefNo, requestWarehouses]);

  // Per-row selection: chosen warehouse and qty to issue
  const [selectedWh, setSelectedWh] = useState<Record<number, number | undefined>>({}); // item_id -> warehouse_id
  const [issueQtyByItem, setIssueQtyByItem] = useState<Record<number, number>>({}); // item_id -> qty

  // Initialize defaults when request or top-level warehouse changes
  useEffect(() => {
    if (!selectedRefNo || !fromWarehouseId) { setSelectedWh({}); setIssueQtyByItem({}); return; }
    const nextWh: Record<number, number | undefined> = {};
    (visibleItems || []).forEach(it => {
      nextWh[it.item_id] = fromWarehouseId;
    });
    setSelectedWh(nextWh);
    // Do NOT clear user-entered quantities here; let them persist unless ref/warehouse changed
    setIssueQtyByItem(prev => {
      // Remove quantities for items no longer visible; keep others
      const keep: Record<number, number> = {};
      (visibleItems || []).forEach(it => { if (prev[it.item_id] != null) keep[it.item_id] = prev[it.item_id]; });
      return keep;
    });
  }, [selectedRefNo, fromWarehouseId]);

  const fulfillColumns: ColumnsType<VwMatGapRequestItemOutstanding> = useMemo(() => [
    { title: 'Mat ID', dataIndex: 'mat_id', key: 'mat_id', width: 110 },
    { title: 'Description', key: 'description', ellipsis: true, render: (_, row) => row.description ?? descMap.get(String(row.mat_id)) ?? '' },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 70 },
    { title: 'Requested', dataIndex: 'requested_qty', key: 'requested_qty', width: 110, align: 'right' },
    { title: 'Issued', dataIndex: 'issued_qty', key: 'issued_qty', width: 100, align: 'right' },
    { title: 'Outstanding', dataIndex: 'outstanding_qty', key: 'outstanding_qty', width: 120, align: 'right' },
    {
      title: 'Warehouse', key: 'warehouse', width: 220,
      render: () => (
        <span>{fromWarehouseId != null ? (whMap.get(Number(fromWarehouseId)) || String(fromWarehouseId)) : '-'}</span>
      )
    },
    {
      title: 'On-hand', key: 'onhand', width: 100, align: 'right',
      render: (_, row) => {
        const wh = fromWarehouseId;
        const inv = (wh == null) ? undefined : (inventory || []).find(x => x.mat_id === row.mat_id && x.warehouse_id === wh);
        return Number(inv?.onhand_qty || 0);
      }
    },
    {
      title: 'Issue Qty', key: 'issue_qty', width: 180, align: 'right',
      render: (_, row) => {
        const wh = fromWarehouseId;
        const inv = (wh == null) ? undefined : (inventory || []).find(x => x.mat_id === row.mat_id && x.warehouse_id === wh);
        const onhand = Number(inv?.onhand_qty || 0);
        const maxQty = Math.min(onhand, Number(row.outstanding_qty || 0));
        const val = issueQtyByItem[row.item_id] ?? 0;
        return (
          <Space>
            <InputNumber
              min={0}
              value={val}
              onChange={(v) => setIssueQtyByItem(prev => ({ ...prev, [row.item_id]: typeof v === 'number' ? v : 0 }))}
              style={{ width: 120 }}
              disabled={!canTransact}
            />
            <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>max {maxQty}</Text>
          </Space>
        );
      }
    },
    {
      title: 'Actions', key: 'actions', width: 120,
      render: (_, row) => {
        const wh = fromWarehouseId;
        const inv = (wh == null) ? undefined : (inventory || []).find(x => x.mat_id === row.mat_id && x.warehouse_id === wh);
        const onhand = Number(inv?.onhand_qty || 0);
        const maxQty = Math.min(onhand, Number(row.outstanding_qty || 0));
        return (
          <Space>
            <Button size="small" onClick={() => setIssueQtyByItem(prev => ({ ...prev, [row.item_id]: maxQty }))} disabled={!canTransact}>All</Button>
          </Space>
        );
      }
    }
  ], [descMap, selectedWh, issueQtyByItem, inventory, whMap]);
  // No expandable rows in the simplified design

  const handleFulfillSubmit = async () => {
    try {
      if (!selectedRefNo) { message.warning('Select a request to fulfill'); return; }
      if (!canTransact) { message.error('You are not authorized to issue from this warehouse.'); return; }
      if (fromWarehouseId == null) { message.warning('Select From Warehouse'); return; }
      if (!issueDate) { message.warning('Select Issue Date'); return; }
      if (!personnelCode) { message.warning('Please select personnel'); return; }
      // Build payloads from per-row selections
      const payloads: any[] = [];
      for (const item of (visibleItems || [])) {
        const qty = Number(issueQtyByItem[item.item_id] || 0);
        if (!qty || qty <= 0) continue;
        const whId = fromWarehouseId;
        if (whId == null) { message.error(`Select a warehouse for item ${item.item_id}`); return; }
        const inv = (inventory || []).find(x => x.mat_id === item.mat_id && x.warehouse_id === whId);
        const onhand = Number(inv?.onhand_qty || 0);
        const maxOutstanding = Number(item.outstanding_qty || 0);
        if (qty > onhand) { message.error(`Qty exceeds on-hand for mat ${item.mat_id} (WH ${whId})`); return; }
        if (qty > maxOutstanding) { message.error(`Qty exceeds outstanding for item ${item.item_id}`); return; }
        payloads.push({
          op_type: 'issue' as const,
          mat_id: item.mat_id,
          qty,
          unit: item.unit || (inv as any)?.unit || 'EA',
          from_warehouse_id: whId,
          txn_date: issueDate || undefined,
          // Set structured linkage and clean assignee
          request_ref_no: selectedRefNo,
          issued_to: personnelCode || null,
        });
      }
      if (payloads.length === 0) { message.warning('Enter allocations to issue'); return; }

      // Get a reference number for this batch
      const seq = await getNextSequenceValue('mat_gap_txns_issue_seq');
      const refNo = `ISS-${String(seq || 0).padStart(7, '0')}`;
      payloads.forEach(p => p.reference_no = refNo);

      await insertData('mat_gap_txns', payloads);
      message.success(`Issued ${payloads.length} lines. Ref ${refNo}`);
      setIssueQtyByItem({});
      // Refresh inventory so on-hand reflects newly issued quantities
      if (typeof refetchInventory === 'function') {
        try { await refetchInventory(); } catch {}
      }
      // Refetch latest data to decide final status from server state
      let freshOutstanding: any = null;
      if (typeof refetchOutstanding === 'function') {
        try {
          freshOutstanding = await refetchOutstanding();
        } catch {}
      }
      if (typeof refetchReqHeaders === 'function') { await refetchReqHeaders(); }
      // Decide status using freshly fetched outstanding rows (preferred), fallback to current cache if necessary
      try {
        const latestAll: VwMatGapRequestItemOutstanding[] = Array.isArray(freshOutstanding?.data)
          ? freshOutstanding.data
          : (Array.isArray(reqOutData) ? (reqOutData as any) : (reqOutData?.data ?? []));
        const latestForRef = (latestAll || []).filter(r => String(r.ref_no || '').trim() === String(selectedRefNo || '').trim());
        const anyOutstanding = latestForRef.some(r => Number(r.outstanding_qty || 0) > 0);
        const nextStatus = anyOutstanding ? 'partially_issued' : 'issued';
        await updateData('mat_gap_issue_requests', String(selectedRefNo || ''), { status: nextStatus }, 'ref_no');
      } catch (e) {
        // Non-fatal; navigation will proceed
      }
      // Navigate back after successful issue
      navigate(-1);
    } catch (e: any) {
      console.error(e);
      message.error(e?.message || 'Failed to issue');
    }
  };

  // Top-level render for fresh UI
  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Fulfill Issue Request</Title>
              <Text type="secondary">Allocate per-warehouse quantities against a selected request</Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Select
              placeholder="From Warehouse"
              allowClear={false}
              value={fromWarehouseId}
              onChange={(v) => setFromWarehouseId(v as number)}
              options={fromWarehouseOptions}
              style={{ width: 240 }}
              showSearch
              optionFilterProp="label"
            />
            <Select
              placeholder="Approved/Partial Requests"
              allowClear
              showSearch
              value={selectedRefNo}
              onChange={(v) => setSelectedRefNo(v as (string | undefined))}
              options={requestOptions}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              style={{ width: 320 }}
              disabled={!fromWarehouseId}
            />
            <span>
              <Text strong>Issue Date</Text>{' '}
              <DatePicker value={issueDate ? dayjs(issueDate) : null} onChange={d => setIssueDate(d ? d.format('YYYY-MM-DD') : null)} style={{ width: 160 }} />
            </span>
            <Input
              placeholder="Issued to Personnel"
              value={personnelDisplay}
              onChange={(e) => setPersonnelDisplay(e.target.value)}
              style={{ width: 260 }}
              suffix={<Button size="small" onClick={() => setPersonnelOpen(true)}>Select</Button>}
            />
            <Button action="add" type="primary" onClick={handleFulfillSubmit} disabled={!canTransact}>Submit Issue</Button>
          </Space>
        </Col>
      </Row>

      <PersonnelSelect
        open={personnelOpen}
        mode="both"
        onCancel={() => setPersonnelOpen(false)}
        onOk={(val: any) => {
          try {
            if (Array.isArray(val)) {
              const [, codeStr, fullName] = val as [string, string, string];
              setPersonnelCode(codeStr ?? null);
              setPersonnelDisplay(fullName ?? '');
            } else {
              const str = String(val);
              const parts = str.split(' - ');
              const codeStr = parts[0] ?? '';
              const fullName = parts.slice(1).join(' - ') || '';
              setPersonnelCode(codeStr || null);
              setPersonnelDisplay(fullName);
            }
          } catch {
            setPersonnelCode(null);
          }
          setPersonnelOpen(false);
        }}
      />

      {selectedRefNo ? (
        <div style={{ marginBottom: 8 }}>
          <Alert
            type={canTransact ? 'info' : 'warning'}
            showIcon
            message={canTransact
              ? 'If the selected request spans multiple warehouses, please separate issues per each warehouse. Only items requested from the selected warehouse are shown.'
              : 'You are not an owner of the selected warehouse. Issuing transactions are restricted.'}
          />
        </div>
      ) : null}

      {!selectedRefNo ? (
        <Card size="small"><Text type="secondary">Select a request to start fulfillment.</Text></Card>
      ) : (
        <Table<VwMatGapRequestItemOutstanding>
          columns={fulfillColumns}
          dataSource={visibleItems}
          rowKey={(r) => String(r.item_id)}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 'max-content' }}
        />
      )}
    </Card>
  );
}
